import React, { memo, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { combineClassNames } from '@utils/combineClassNames';
import { useTrialGeneration } from '../../hooks/use-trial-generation';
import { QRCodeSVG } from '@common/components/qr-code';
import { buildStatusLabel, displayPercent, toSelectedBuildModules, withSharedGateProgress } from '../../utils/trial-build';
import { TRIAL_APP_ANDROID, TRIAL_APP_IOS, trialAppDownloadUrl } from '../../utils/trial-app-store';
import { TRIAL_DOWNLOAD_PATH, TRIAL_OPEN_PATH, TrialStrokeIcon } from './trial-icons';
import { TrialDemoPlayer } from './trial-demo-player';
import { TRIAL_WORDMARK } from './trial-media';
import { BrandLogo } from './brand-logo';
import type { TrialGenerationStatus, TrialModuleKey } from '../../types';

const PHONE_STEPS = [
  { key: 'scan', delay: '1.55s', tone: 'brand' },
  { key: 'install', delay: '1.7s', tone: 'muted' },
  { key: 'login', delay: '1.85s', tone: 'muted' },
] as const;

type Props = {
  company: string;
  brand: string;
  iconUrl?: string | null;
  logoUrl?: string | null;
  languageLocale?: string;
  email?: string;
  organisationId?: string | null;
  initialGeneration?: TrialGenerationStatus | null;
  selectedModules?: TrialModuleKey[];
  interactive?: boolean;
  isEntering?: boolean;
  enterError?: 'networks' | 'failed' | null;
  onEnterAdmin?: () => void;
  onEnterUser?: () => void;
  /** Logo and brand colour exist only after the branding step has finished. */
  onBrandingReady?: () => void;
};

export const BuildBackdrop = memo(({
  company,
  brand,
  iconUrl,
  logoUrl,
  languageLocale,
  email,
  organisationId,
  initialGeneration,
  selectedModules = [],
  interactive,
  isEntering,
  enterError,
  onEnterAdmin,
  onEnterUser,
  onBrandingReady,
}: Props) => {
  const { t } = useTranslation();
  const { generation, isReady, isFailed, isStalled } = useTrialGeneration(
    organisationId || '',
    initialGeneration ?? null,
  );
  const brandingDone = (generation?.steps ?? []).some(
    (step) => step.phase === 'gate' && step.key === 'branding' && step.status === 'done',
  );

  useEffect(() => {
    if (brandingDone) onBrandingReady?.();
  }, [brandingDone, onBrandingReady]);

  const modules = withSharedGateProgress(
    toSelectedBuildModules(
      generation?.steps,
      selectedModules,
      (key) => t(`authentication:trial_module_${key}`),
    ),
    generation?.percent,
    generation?.steps,
  );
  const hasFailedStep = isFailed || modules.some((mod) => mod.failed);
  const modulesSettled = modules.length > 0 && modules.every((mod) => mod.done || mod.failed);
  const showReady = !!interactive && (isReady || isFailed || modulesSettled);
  const pct = displayPercent(
    generation?.percent,
    showReady,
    generation?.steps,
    !!generation,
  );
  const initial = company.trim().charAt(0).toUpperCase() || 'A';
  const minutes = Math.max(1, Math.ceil((100 - pct) / 25));
  const doneCount = modules.filter((mod) => mod.done).length;
  const nowLabel = buildStatusLabel(generation?.steps, modules);
  const downloadUrl = trialAppDownloadUrl();
  const [didGetApp, setDidGetApp] = useState(false);

  return (
    <div
      className={combineClassNames('FreeTrial__Build max-md:!tw-gap-3 max-md:!tw-p-4', {
        'FreeTrial__Build--live': interactive,
        'FreeTrial__Build--ready': showReady,
      })}
      aria-hidden={!interactive}
    >
      <svg className="FreeTrial__Build__Dots" width="100%" height="100%">
        <defs>
          <pattern id="trial-build-dots" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="#c7d2fe" fillOpacity=".12" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#trial-build-dots)" />
        <circle cx="12%" cy="20%" r="420" fill="#2463eb" fillOpacity=".07" />
      </svg>

      <header className="FreeTrial__Build__Head">
        <img src={TRIAL_WORDMARK} alt="" />
        <span style={{ '--trial-brand': brand } as React.CSSProperties}>
          {iconUrl || logoUrl ? (
            <BrandLogo
              className="FreeTrial__Build__Logo"
              iconUrl={iconUrl}
              logoUrl={logoUrl}
              name={company}
            />
          ) : (
            <em style={{ backgroundColor: brand }}>{initial}</em>
          )}
          {t('authentication:trial_build_app_name', { name: company })}
        </span>
      </header>

      <div className="FreeTrial__Build__Main max-md:!tw-flex-col max-md:!tw-items-stretch max-md:!tw-gap-[22px]">
        <section className={combineClassNames('FreeTrial__Build__VideoCol', { 'max-md:!tw-hidden': showReady })}>
          <TrialDemoPlayer
            languageLocale={languageLocale}
            title={t('authentication:trial_build_demo')}
            watchLabel={t('authentication:trial_build_watch')}
            demoLabel={t('authentication:trial_build_demo')}
          />
          <div
            className={combineClassNames('FreeTrial__Build__Download', {
              'FreeTrial__Build__Download--ready': showReady,
              'max-md:!tw-hidden': showReady,
            })}
          >
            <a
              className="FreeTrial__Build__Qr max-md:tw-hidden"
              href={downloadUrl}
              tabIndex={interactive ? undefined : -1}
              aria-label={t('authentication:trial_build_scan_title')}
            >
              <QRCodeSVG
                value={downloadUrl}
                size={92}
                bgColor="#ffffff"
                fgColor="#19214d"
                includeMargin
                level="M"
              />
            </a>
            <div>
              <strong>{t('authentication:trial_build_download')}</strong>
              <p className="max-md:tw-hidden">
                {t('authentication:trial_build_download_hint', { name: company, email: email || '' })}
              </p>
              <p className="FreeTrial__Build__DownloadHint">
                {t('authentication:trial_build_download_hint_short')}
              </p>
            </div>
            <div className="FreeTrial__Build__Stores max-md:tw-hidden">
              <a href={TRIAL_APP_IOS} tabIndex={interactive ? undefined : -1} target="_blank" rel="noreferrer">
                <span className="FreeTrial__Build__StorePlate">
                  <img src="/static/images/app_store.png" alt={t('authentication:trial_build_iphone')} />
                </span>
              </a>
              <a href={TRIAL_APP_ANDROID} tabIndex={interactive ? undefined : -1} target="_blank" rel="noreferrer">
                <img src="/static/images/play_store.png" alt={t('authentication:trial_build_android')} />
              </a>
            </div>
            <a
              className="FreeTrial__GetApp max-md:!tw-ml-auto max-md:!tw-inline-flex max-md:!tw-flex-none"
              href={downloadUrl}
              tabIndex={interactive ? undefined : -1}
              onClick={() => setDidGetApp(true)}
            >
              <TrialStrokeIcon className="FreeTrial__GetApp__Icon" d={TRIAL_DOWNLOAD_PATH} size={16} strokeWidth={2.2} />
              {t('authentication:trial_build_get_app')}
            </a>
          </div>
        </section>

        <section
          className={combineClassNames(
            'FreeTrial__Build__Progress max-md:!tw-max-w-none max-md:!tw-border-0 max-md:!tw-bg-transparent max-md:!tw-p-0',
            showReady ? 'max-md:!tw-flex max-md:!tw-min-h-0 max-md:!tw-flex-1 max-md:!tw-flex-col' : 'max-md:!tw-flex-none',
          )}
        >
          {!showReady && (
            <>
              <p>{t('authentication:trial_build_title', { name: company })}</p>
              <div className="FreeTrial__Build__Meter">
                <b>{pct}%</b>
                <span>
                  <i />
                  {t(minutes === 1
                    ? 'authentication:trial_build_minute_left'
                    : 'authentication:trial_build_minutes_left', { count: minutes })}
                </span>
              </div>
              <div className="FreeTrial__Build__Bar"><em style={{ width: `${pct}%` }} /></div>
              <small>
                {nowLabel
                  ? t('authentication:trial_build_status', {
                    name: nowLabel, done: doneCount, total: modules.length,
                  })
                  : t('authentication:trial_build_modules_ready', { done: doneCount, total: modules.length })}
              </small>
              {hasFailedStep && <p className="FreeTrial__Build__Fail">{t('authentication:trial_building_failed_headline')}</p>}
              {isStalled && <p className="FreeTrial__Build__Fail">{t('authentication:trial_building_connection_lost')}</p>}
              <ul>
                {modules.map((mod) => (
                  <li
                    key={mod.key}
                    className={combineClassNames('FreeTrial__Build__Mod', {
                      'FreeTrial__Build__Mod--done': mod.done,
                      'FreeTrial__Build__Mod--on': mod.building,
                      'FreeTrial__Build__Mod--fail': mod.failed,
                    })}
                    title={mod.statusText || undefined}
                  >
                    <span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                        <path d={mod.d} />
                      </svg>
                    </span>
                    <div className="FreeTrial__Build__ModCopy">
                      <div className="FreeTrial__Build__ModMeta">
                        <strong>{mod.label}</strong>
                        {mod.done && (
                          <svg
                            className="FreeTrial__Build__ModTick"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#7ea3ff"
                            strokeWidth="3.2"
                          >
                            <path d="M5 12l5 5 9-10" />
                          </svg>
                        )}
                        {mod.failed && (
                          <svg
                            className="FreeTrial__Build__ModTick"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fda4af"
                            strokeWidth="3.2"
                          >
                            <path d="M6 6l12 12M18 6L6 18" />
                          </svg>
                        )}
                        <em>{mod.failed ? t('authentication:trial_build_failed') : `${mod.progress}%`}</em>
                      </div>
                      <div className="FreeTrial__Build__ModBar"><b style={{ width: `${mod.progress}%` }} /></div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {showReady && (
            <div className="FreeTrial__Build__Done">
              <ul className="FreeTrial__Build__Collapse" aria-hidden="true">
                {modules.map((mod) => (
                  <li key={mod.key} style={{ animationDelay: mod.collapseDelay }}>
                    <span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2463eb" strokeWidth="2.2">
                        <path d={mod.d} />
                      </svg>
                    </span>
                    <strong>{mod.label}</strong>
                  </li>
                ))}
              </ul>

              <div className="FreeTrial__Build__ReadyHead">
                <span className="FreeTrial__Build__Check">
                  <i />
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <path d="M5 12l5 5 9-10" />
                  </svg>
                </span>
                <div>
                  <p>{t('authentication:trial_build_all_modules', { total: modules.length })}</p>
                  <h1>{t('authentication:trial_build_ready_title', { name: company })}</h1>
                  {email && (
                    <span className="FreeTrial__Build__LoginWith">
                      {t('authentication:trial_build_login_with', { email })}
                    </span>
                  )}
                </div>
              </div>
              <a
                className={
                  'FreeTrial__GetApp FreeTrial__GetApp--ready max-md:!tw-inline-flex '
                  + 'max-md:!tw-h-[58px] max-md:!tw-w-full'
                }
                href={downloadUrl}
                tabIndex={interactive ? undefined : -1}
                onClick={() => setDidGetApp(true)}
              >
                {didGetApp ? (
                  <>
                    {t('authentication:trial_build_open_app')}
                    <TrialStrokeIcon className="FreeTrial__GetApp__Open" d={TRIAL_OPEN_PATH} size={18} strokeWidth={2.2} />
                  </>
                ) : (
                  <>
                    <TrialStrokeIcon className="FreeTrial__GetApp__Icon" d={TRIAL_DOWNLOAD_PATH} size={19} strokeWidth={2.2} />
                    {t('authentication:trial_build_download_cta')}
                  </>
                )}
              </a>
              {didGetApp && (
                <p className="FreeTrial__Build__NotInstalled">
                  <Trans
                    i18nKey="authentication:trial_build_not_installed"
                    components={[<a href={downloadUrl} tabIndex={interactive ? undefined : -1} />]}
                  />
                </p>
              )}

              <div className="FreeTrial__Build__Group">
                <p>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#c7d2fe" strokeWidth="2">
                    <path d="M7 2h10v20H7zM11 18h2" />
                  </svg>
                  {t('authentication:trial_build_on_phone')}
                </p>
                <ol>
                  {PHONE_STEPS.map((step, index) => (
                    <li key={step.key} style={{ animationDelay: step.delay }}>
                      <em className={step.tone === 'brand' ? 'is-brand' : undefined}>{index + 1}</em>
                      <span>
                        <strong>{t(`authentication:trial_build_step_${step.key}_title`)}</strong>
                        <small>{t(`authentication:trial_build_step_${step.key}_text`, { email: email || '' })}</small>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="FreeTrial__Build__Web">
                {enterError && (
                  <p className="FreeTrial__Build__Fail" role="alert">
                    {t(enterError === 'networks'
                      ? 'authentication:networks_select_error'
                      : 'common:something_went_wrong')}
                  </p>
                )}
                <p>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#c7d2fe" strokeWidth="2">
                    <path d="M3 5h18v12H3zM8 21h8M12 17v4" />
                  </svg>
                  {t('authentication:trial_build_on_web')}
                </p>
                <div>
                  <button type="button" onClick={onEnterAdmin} disabled={isEntering}>
                    <span>{t('authentication:trial_build_recommended')}</span>
                    <strong>
                      {t('authentication:trial_build_admin')}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2463eb" strokeWidth="2.2">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </strong>
                  </button>
                  <button type="button" className="is-ghost" onClick={onEnterUser} disabled={isEntering}>
                    <strong>
                      {t('authentication:trial_build_user')}
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c7d2fe" strokeWidth="2.2">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </strong>
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
});
